import { formatUserFacingError } from '@/lib/errors'
import { useState, useEffect, useCallback } from 'react'
import type { ApplicationSubmissionDocument } from '@/types'
import { adminSubmissionsService } from '../services/admin.submissions.service'

interface UseAdminSubmissionsResult {
  submissions: Array<ApplicationSubmissionDocument & { id: string }>
  loading: boolean
  error: string | null
  refresh: () => void
  updateStatus: (id: string, status: ApplicationSubmissionDocument['status']) => Promise<void>
  deleteSubmission: (id: string) => Promise<void>
}

export function useAdminSubmissions(applicationId: string): UseAdminSubmissionsResult {
  const [submissions, setSubmissions] = useState<Array<ApplicationSubmissionDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    if (!applicationId) return
    setLoading(true)
    setError(null)
    adminSubmissionsService
      .getByApplicationId(applicationId)
      .then(setSubmissions)
      .catch((err) => setError(formatUserFacingError(err, 'Failed to load submissions')))
      .finally(() => setLoading(false))
  }, [applicationId, tick])

  const updateStatus = useCallback(
    async (id: string, status: ApplicationSubmissionDocument['status']) => {
      await adminSubmissionsService.updateStatus(id, status)
      refresh()
    },
    [refresh]
  )

  const deleteSubmission = useCallback(async (id: string) => {
    await adminSubmissionsService.delete(id)
    refresh()
  }, [refresh])

  return { submissions, loading, error, refresh, updateStatus, deleteSubmission }
}
